import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { cn } from "@shared/lib/cn";

/*
 * Окно поверх приложения.
 *
 * Три окна на нём: вопрос, настройки и выбор экрана для показа. Общего у них
 * ровно то, что здесь и лежит, — портал, затемнение, место по центру и слой.
 * Раньше каждое писало это себе само, а `Dialog` брало напрямую из `radix-ui`;
 * прослойки не было, и одинаковое разошлось бы при первой же правке.
 *
 * Величина и поверхность приходят от вызова: у вопроса 440px и мягкая тень, у
 * настроек — окно с рантом на 660, у выбора экрана — 720 под сетку превью. Это
 * не варианты одного, а три разных окна, и сводить их к одному набору значило
 * бы придумать вид, которого в макете нет.
 *
 * Слои ложатся друг на друга порядком в DOM, а не числами: слой у затемнения и
 * у окна один и тот же, и портал, открытый позже, оказывается выше. Иначе
 * вопрос, заданный поверх настроек, пришлось бы нумеровать вручную — и его
 * затемнение ушло бы под окно, которое оно затемняет.
 */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-40 bg-scrim",
        // Затемнение только тает: ехать ему некуда, оно во весь экран.
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-40 -translate-x-1/2 -translate-y-1/2",
          /*
           * Появляется на месте, чуть подрастая пружиной (`glass-dialog` в
           * `ui.css`), — и только масштабом, без растворения: окно стеклянное,
           * а полупрозрачный предок отрезает стеклу фон. Окно настроек
           * вырастает из шестерёнки своей анимацией (признак `data-zoom`).
           *
           * Сдвиг центрирования лежит в `translate`, а анимация пишет в
           * `transform` — разные свойства, они не спорят.
           */
          "glass-dialog flex flex-col outline-none",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/** Вид вопроса: узкое окно с полями и мягкой тенью. */
const DIALOG_BOX = [
  "w-[min(440px,calc(100%-var(--space-8)))] max-h-[84vh]",
  // 20px, а не ступень: `px-5` даёт 14px при плотности 0.70×.
  "gap-3 px-[22px] py-[20px]",
  // Материал «Почты» (`surface-pop`), без стекла движка: стекло окна стоило кадров, а видно его не было.
  "surface-pop rounded-[26px]",
].join(" ");

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("font-[var(--font-heading)] text-[18px] font-medium", className)}
      {...props}
    />
  );
}

/** Действия внизу окна: главное — последним, у правого края. */
function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("mt-2 flex justify-end gap-2", className)}
      {...props}
    />
  );
}

export {
  DIALOG_BOX,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
};
